<template>
    <div>
        <report-header
            :title="title"
            :isLoading="isLoading"
            :reportPagination="pagination"
            :isShowDateRange="true"
            :formatDateRangeTitle="dateRange"
        />

        <b-card id="report-card">
            <div v-if="!loaded">
                <p class="mt-3">
                    Please select the
                    <a @click="openFilterModal" class="text-link">filter</a>
                </p>
            </div>
            <div id="report-body" class="w-100 mt-3 mb-3">
                <div v-if="loaded">
                    <div v-if="length > 0">
                        <QTable
                            :data="result"
                            :columns="columns"
                            :loading="isLoading"
                            :rows-per-page-options="[0]"
                            row-key="index"
                            hide-bottom
                        >
                            <template v-slot:body-cell-module="props">
                                <q-td
                                    :props="props"
                                    class="text-capitalize"
                                    v-if="
                                        props.row.module.includes(
                                            'Invoice #'
                                        ) ||
                                        props.row.module.includes('Estimate #')
                                    "
                                >
                                    <span
                                        v-for="(item,
                                        itemIndex) in generateInvoiceEstimateData(
                                            props.row.module
                                        )"
                                        :key="itemIndex"
                                    >
                                        <a
                                            v-if="
                                                item.encodedId &&
                                                item.module === 'invoice'
                                            "
                                            :href="
                                                '../apps/billing/invoice/view/' +
                                                item.encodedId
                                            "
                                        >
                                            {{ item.id }}
                                        </a>
                                        <a
                                            v-else-if="
                                                item.encodedId &&
                                                item.module === 'estimate'
                                            "
                                            :href="
                                                '../apps/billing/estimate/view/' +
                                                item.encodedId
                                            "
                                        >
                                            {{ item.id }}
                                        </a>
                                        <span v-else>
                                            {{ props.row.module }}
                                        </span>
                                    </span>
                                </q-td>
                                <q-td
                                    :props="props"
                                    class="text-capitalize"
                                    v-else
                                >
                                    {{ props.row.module }}
                                </q-td>
                            </template>
                        </QTable>
                    </div>
                    <div v-else>
                        <QTable
                            :data="[]"
                            :columns="columns"
                            :loading="isLoading"
                            :rows-per-page-options="[0]"
                            row-key="index"
                            hide-bottom
                        ></QTable>
                        <div class="py-3" v-if="!isLoading">
                            <i>No records available</i>
                        </div>
                    </div>
                </div>
            </div>
        </b-card>
    </div>
</template>

<script>
const REPORT_NAME = "Deleted Transactions";
import { isEmpty } from "lodash";
import { DateTime } from "luxon";
import { QTable } from "quasar";
import Report from "@/mixins/report.js";
import ReportService from "@/services/report";
import CREPORT from "@/constants/report";
export default {
    title: REPORT_NAME,
    components: {
        QTable,
        ReportHeader: () => import("@/components/report/report-header")
    },
    props: {
        data: {
            type: Object,
            default() {
                return {
                    start: moment(),
                    end: moment(),
                    module: {
                        value: 1,
                        text: "Schedule"
                    }
                };
            }
        }
    },
    mixins: [Report],
    data() {
        return {
            locale: "en-US",
            loaded: false,
            isLoading: false,
            indexAxios: 0,
            dateRange: "",
            meta: "",
            result: [],
            total: 0,
            length: 0,
            abortController: [],
            page: 1,
            pagination: {},
            code: "",
            params: "",
            title: REPORT_NAME,
            columns: [
                {
                    name: "description",
                    field: "description",
                    label: "Record",
                    align: "left",
                    style: "vertical-align: top; min-width: 200px;"
                },
                {
                    name: "module",
                    field: "module",
                    label: "Module",
                    align: "left",
                    style: "vertical-align: top; min-width: 110px;"
                },
                {
                    name: "update_at",
                    field: "update_at",
                    label: "Deleted",
                    align: "left",
                    style: "vertical-align: top"
                },
                {
                    name: "update_by",
                    field: "update_by",
                    label: "Deleted By",
                    align: "left",
                    style: "vertical-align: top"
                }
            ]
        };
    },
    mounted() {
        let self = this;
        // --- toggle navigation options
        self.data.filterDateOption = true;
        self.data.isShowDateRange = true;
        self.data.isShowModuleOption = true;
        self.data.reportId = CREPORT.TYPE.DELETED_TRANSACTIONS;
        self.toggleGreyOutButton(true);

        self._getSetting().then(async () => {
            self.data.start = moment().tz(this._timeZone).startOf("day");
            self.data.end = moment().tz(this._timeZone).endOf("day");
            // if query is not empty, load the report
            // else open the filter modal before loading the report
            if (
                !isEmpty(this.$route.query.to) &&
                !isEmpty(this.$route.query.from)
            ) {
                self.init();
            } else {
                self.openFilterModal();
            }
        });

        self.$root.$on(
            "report-filter-date-range:refresh",
            self.handleOnFilterChanged
        );
        self.$root.$on("deleted_transactions:refresh", self.handleOnRefresh);
    },
    beforeDestroy: function () {
        let self = this;
        self.$root.$off("report-filter-date-range:refresh", self.init);
        self.data.isShowModuleOption = false;

        self.resetPagination();

        if (self.abortController.length > 0) {
            self.abortController[self.indexAxios].abort();
        }
        self.toggleGreyOutButton(false);
    },
    methods: {
        async init(isRefresh = 0, indexAxios) {
            let self = this;

            clearInterval(self.pagination.interval);
            self.loadingPercentages();
            self.abortController[indexAxios] = new AbortController();

            await ReportService.getReport(CREPORT.TYPE.DELETED_TRANSACTIONS, {
                ...self.params,
                refresh: isRefresh ? 1 : 0,
                page: self.pagination.page,
                signal: self.abortController[indexAxios].signal
            })
                .then(response => {
                    const reportResponse = response.resource;
                    const length = reportResponse.results.length;

                    if (response.message && response.message.code === 200) {
                        self.meta = reportResponse.meta;
                        self.locale = self.meta.locale.replace("_", "-");
                        self.title = self.capitalize(self.meta.title);
                        self.dynamicPageTitle(self.title);

                        self.pagination.lastPage =
                            reportResponse.paginate.lastPage;
                        self.pagination.page++;

                        if (length == 0) {
                            self.toggleGreyOutButton(true);
                        } else {
                            if (length > 0) {
                                reportResponse.results.forEach(e => {
                                    self.result.push({
                                        description: e.description,
                                        module: e.module,
                                        update_at: e.update_at,
                                        update_by: e.update_by
                                    });
                                });
                                self.length = self.result.length;
                                self.toggleGreyOutButton(false);
                            }
                        }
                    }

                    if (self.pagination.page <= self.pagination.lastPage) {
                        self.timer(self.INTERVAL_TIME).then(() => {
                            self.init(isRefresh);
                        });
                    } else {
                        self.loadingCompleted();
                        self.isLoading = false;
                    }
                })
                .catch(e => {
                    if (!self.abortController[indexAxios].signal.aborted) {
                        console.error(e);
                        self.loaded = true;
                        self._message(
                            "Error: Unable to get the report",
                            "danger"
                        );
                    }
                });
        },
        handleOnFilterChanged(params) {
            let self = this;
            self.isLoading = true;
            self.loaded = true;
            self.result = [];
            self.pagination = self.DEFAULT_PAGINATION();
            self.toggleGreyOutButton(true);
            self.length = 0;

            if (params) {
                self.data.from = params.from;
                self.data.to = params.to;
                self.data.start = params.start;
                self.data.end = params.end;
                self.data.module = params.module;
            }

            self.params = {
                ...self.params,
                from: params.from,
                to: params.to,
                module: params.module.map(id => id.value),
                perPage: 100
            };

            const from = DateTime.fromSeconds(params.from);
            const to = DateTime.fromSeconds(params.to);

            // set selected date range
            self.dateRange = `(${from.toFormat(
                self._dateFormat
            )} - ${to.toFormat(self._dateFormat)})`;

            self.init(0, self.indexAxios);
        },
        handleOnRefresh() {
            let self = this;
            self.isLoading = true;
            self.loaded = true;
            self.result = [];
            self.pagination = self.DEFAULT_PAGINATION();
            self.toggleGreyOutButton(true);
            self.length = 0;
            if (self.abortController.length > 0) {
                self.abortController[self.indexAxios].abort();
            }
            self.indexAxios++;
            self.init(1, self.indexAxios);
        },
        generateInvoiceEstimateData(moduleName) {
            let result = [];
            let invoice = moduleName.split("@#");
            let module = "invoice";
            if (moduleName.includes("Estimate #")) {
                module = "estimate";
            }
            if (invoice.length > 1) {
                result.push({
                    encodedId: invoice[0],
                    id: invoice[1],
                    module: module
                });
            } else {
                result.push({
                    id: moduleName
                });
            }
            return result;
        },
        toggleGreyOutButton(value) {
            let self = this;
            // to grey out the action menu when there is no data / loading
            self.data.isGreyOutButton = value;
        }
    }
};
</script>
